/* ==========================================================================
   Premier UI (2026 rebuild): announcement bar, header + mega menu, menu and
   cart drawers, predictive search, AJAX cart and product-card quick add.
   Vanilla JS, no dependencies. Exposes window.PremTheme for other scripts
   (prem-product.js uses PremTheme.cart and PremTheme.openDrawer).
   ========================================================================== */
(function () {
  'use strict';

  var d = document;
  var P = (window.PremTheme = window.PremTheme || {});
  var routes = Object.assign({
    root: '/',
    cart: '/cart',
    cart_add: '/cart/add',
    cart_change: '/cart/change',
    cart_update: '/cart/update',
    search: '/search',
    predictive_search: '/search/suggest'
  }, P.routes || {});
  var CART_SECTION = 'prem-cart-drawer';
  var CART_DRAWER_ID = 'PremCartDrawer';

  function qs(sel, el) { return (el || d).querySelector(sel); }
  function qsa(sel, el) { return Array.prototype.slice.call((el || d).querySelectorAll(sel)); }
  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function dispatch(name, detail) { d.dispatchEvent(new CustomEvent(name, { detail: detail || {} })); }

  /* ---------- Money ---------- */
  P.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    format = format || P.moneyFormat || '${{amount}}';
    function delimit(n, precision, thousands, decimal) {
      var parts = (n / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[0] + (parts[1] ? decimal + parts[1] : '');
    }
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, key) {
      switch (key) {
        case 'amount_no_decimals': return delimit(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return delimit(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(cents, 0, '.', ',');
        default: return delimit(cents, 2, ',', '.');
      }
    });
  };

  /* ---------- Toast ---------- */
  var toastEl, toastTimer;
  P.toast = function (msg) {
    if (!toastEl) {
      toastEl = d.createElement('div');
      toastEl.className = 'prem-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      d.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-visible'); }, 4500);
  };

  /* ---------- Overlays + scroll lock ---------- */
  var overlays = {};
  function overlay(kind) {
    if (overlays[kind]) return overlays[kind];
    var el = d.createElement('div');
    el.className = 'prem-overlay' + (kind === 'drawer' ? ' prem-overlay--top' : '');
    el.setAttribute('aria-hidden', 'true');
    d.body.appendChild(el);
    overlays[kind] = el;
    return el;
  }

  var locks = 0;
  function lock() { if (locks++ === 0) d.documentElement.classList.add('prem-lock'); }
  function unlock() { locks = Math.max(0, locks - 1); if (!locks) d.documentElement.classList.remove('prem-lock'); }

  function focusables(el) {
    return qsa('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])', el)
      .filter(function (n) { return n.offsetParent !== null || n === d.activeElement; });
  }

  /* ==========================================================================
     Announcement bar
     ========================================================================== */
  function initAnnouncement() {
    var bar = qs('[data-prem-announce]');
    if (!bar) return;
    var key = 'prem-announce-closed:' + (bar.getAttribute('data-dismiss-key') || '');
    try { if (sessionStorage.getItem(key)) { bar.remove(); return; } } catch (e) {}

    var now = Date.now();
    qsa('[data-prem-slide]', bar).forEach(function (s) {
      var end = Date.parse(s.getAttribute('data-ends') || '');
      if (end && s.hasAttribute('data-hide-after') && end < now) s.remove();
    });
    var slides = qsa('[data-prem-slide]', bar);
    if (!slides.length) { bar.remove(); return; }
    slides[0].classList.add('is-active');

    var word = bar.getAttribute('data-countdown-label') || 'left';
    function tick() {
      slides.forEach(function (s) {
        var timer = qs('[data-prem-timer]', s);
        var end = Date.parse(s.getAttribute('data-ends') || '');
        if (!timer || !end) return;
        var diff = end - Date.now();
        if (diff <= 0) { timer.hidden = true; return; }
        var days = Math.floor(diff / 864e5);
        var hrs = Math.floor((diff % 864e5) / 36e5);
        var mins = Math.floor((diff % 36e5) / 6e4);
        qs('[data-prem-timer-text]', timer).textContent = days > 0
          ? days + (days === 1 ? ' day ' : ' days ') + word
          : hrs + 'h ' + mins + 'm ' + word;
        timer.hidden = false;
      });
    }
    tick();
    setInterval(tick, 60000);

    if (slides.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      var i = 0, paused = false;
      ['mouseenter', 'focusin'].forEach(function (ev) { bar.addEventListener(ev, function () { paused = true; }); });
      ['mouseleave', 'focusout'].forEach(function (ev) { bar.addEventListener(ev, function () { paused = false; }); });
      setInterval(function () {
        if (paused) return;
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }, (parseInt(bar.getAttribute('data-interval'), 10) || 6) * 1000);
    }

    var close = qs('[data-prem-announce-close]', bar);
    if (close) close.addEventListener('click', function () {
      try { sessionStorage.setItem(key, '1'); } catch (e) {}
      bar.remove();
    });
  }

  /* ==========================================================================
     Header: sticky state + mega menu
     ========================================================================== */
  var closeMega = function () {};
  var closeSearch = function () {};

  function initHeader() {
    var header = qs('[data-prem-header]');
    if (!header) return;

    var onScroll = function () { header.classList.toggle('is-scrolled', window.scrollY > 30); };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    var items = qsa('[data-prem-mega-item]', header);
    if (!items.length) return;
    var desktop = window.matchMedia('(min-width: 1025px)');
    var ov = overlay('mega');
    var openItem = null, openTimer, closeTimer, lastPointer = 'mouse';

    function setExpanded(item, state) {
      qsa('[data-prem-mega-toggle]', item).forEach(function (b) { b.setAttribute('aria-expanded', state ? 'true' : 'false'); });
    }
    function open(item) {
      clearTimeout(closeTimer);
      if (openItem === item) return;
      if (openItem) { openItem.classList.remove('is-open'); setExpanded(openItem, false); }
      closeSearch();
      item.classList.add('is-open');
      setExpanded(item, true);
      openItem = item;
      ov.classList.add('is-visible');
    }
    function close() {
      clearTimeout(openTimer);
      if (!openItem) return;
      openItem.classList.remove('is-open');
      setExpanded(openItem, false);
      openItem = null;
      ov.classList.remove('is-visible');
    }
    closeMega = close;

    header.addEventListener('pointerdown', function (e) { lastPointer = e.pointerType || 'mouse'; }, true);

    items.forEach(function (item) {
      item.addEventListener('mouseenter', function (e) {
        if (!desktop.matches || lastPointer === 'touch') return;
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        openTimer = setTimeout(function () { open(item); }, openItem ? 0 : 90);
      });
      item.addEventListener('mouseleave', function () {
        if (lastPointer === 'touch') return;
        clearTimeout(openTimer);
        closeTimer = setTimeout(close, 200);
      });

      qsa('[data-prem-mega-toggle]', item).forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          if (openItem === item) close(); else open(item);
        });
      });

      // Touch on a linked top-level item: first tap opens the panel, second tap follows the link.
      var link = qs('a[data-prem-mega-trigger]', item);
      if (link) link.addEventListener('click', function (e) {
        if (lastPointer === 'touch' && openItem !== item) { e.preventDefault(); open(item); }
      });

      item.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' && e.target.matches('[data-prem-mega-trigger], [data-prem-mega-toggle]')) {
          e.preventDefault();
          open(item);
          var first = focusables(qs('[data-prem-mega]', item))[0];
          if (first) first.focus();
        }
      });

      item.addEventListener('focusout', function (e) {
        if (openItem === item && e.relatedTarget && !item.contains(e.relatedTarget)) close();
      });
    });

    ov.addEventListener('click', function () { close(); closeSearch(); });
    ov.addEventListener('mouseenter', function () { if (lastPointer !== 'touch') { clearTimeout(closeTimer); closeTimer = setTimeout(close, 120); } });
    d.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !openItem) return;
      var toggle = qs('[data-prem-mega-toggle]', openItem);
      close();
      if (toggle) toggle.focus();
    });
    d.addEventListener('click', function (e) {
      if (openItem && !openItem.contains(e.target)) close();
    });
    if (desktop.addEventListener) desktop.addEventListener('change', close);
  }

  /* ==========================================================================
     Search overlay + predictive search
     ========================================================================== */
  function initSearch() {
    var panel = qs('[data-prem-search]');
    if (!panel) return;
    var input = qs('[data-prem-search-input]', panel);
    var results = qs('[data-prem-search-results]', panel);
    var popular = qs('[data-prem-search-popular]', panel);
    var openers = qsa('[data-prem-search-open]');
    var ov = overlay('mega');
    var lastOpener = null;

    function isOpen() { return panel.classList.contains('is-open'); }
    function open(opener) {
      closeMega();
      lastOpener = opener || null;
      panel.classList.add('is-open');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
      ov.classList.add('is-visible');
      setTimeout(function () { input.focus(); }, 60);
    }
    function close(restore) {
      if (!isOpen()) return;
      panel.classList.remove('is-open');
      openers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
      ov.classList.remove('is-visible');
      if (restore && lastOpener) lastOpener.focus();
    }
    closeSearch = close;

    openers.forEach(function (b) {
      b.addEventListener('click', function () { if (isOpen()) close(true); else open(b); });
    });
    qs('[data-prem-search-close]', panel).addEventListener('click', function () { close(true); });
    d.addEventListener('keydown', function (e) { if (e.key === 'Escape' && isOpen()) close(true); });
    d.addEventListener('click', function (e) {
      if (isOpen() && !panel.contains(e.target) && !e.target.closest('[data-prem-search-open]')) close();
    });

    var ctrl = null, timer = null;
    input.addEventListener('input', function () {
      clearTimeout(timer);
      var q = input.value.trim();
      if (q.length < 2) {
        results.innerHTML = '';
        if (popular) popular.hidden = false;
        return;
      }
      timer = setTimeout(function () { fetchResults(q); }, 180);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') {
        var first = qs('a', results);
        if (first) { e.preventDefault(); first.focus(); }
      }
    });
    results.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      var links = qsa('a', results);
      var idx = links.indexOf(d.activeElement);
      if (idx < 0) return;
      e.preventDefault();
      if (e.key === 'ArrowUp' && idx === 0) { input.focus(); return; }
      var next = links[idx + (e.key === 'ArrowDown' ? 1 : -1)];
      if (next) next.focus();
    });

    function fetchResults(q) {
      if (ctrl) ctrl.abort();
      ctrl = window.AbortController ? new AbortController() : null;
      var url = routes.predictive_search + '.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product,collection,page,article&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=last';
      fetch(url, { signal: ctrl ? ctrl.signal : undefined, headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) { render(q, (data.resources && data.resources.results) || {}); })
        .catch(function (err) { if (!err || err.name !== 'AbortError') results.innerHTML = ''; });
    }

    function item(url, title, meta, img) {
      return '<li><a class="prem-search__item" href="' + esc(url) + '">' +
        (img ? '<img src="' + esc(img) + (img.indexOf('?') > -1 ? '&' : '?') + 'width=120" alt="" width="56" height="56">' : '') +
        '<span><span class="prem-search__item-title">' + esc(title) + '</span>' +
        (meta ? '<span class="prem-search__item-meta">' + meta + '</span>' : '') +
        '</span></a></li>';
    }

    function render(q, res) {
      var products = res.products || [];
      var collections = (res.collections || []).slice(0, 3);
      var pages = (res.pages || []).concat(res.articles || []);
      if (popular) popular.hidden = true;
      var allUrl = routes.search + '?q=' + encodeURIComponent(q) + '&options%5Bprefix%5D=last';

      if (!products.length && !collections.length && !pages.length) {
        results.innerHTML = '<p class="prem-search__empty">No results for &ldquo;' + esc(q) + '&rdquo;. Try &ldquo;plyo&rdquo;, &ldquo;weighted&rdquo; or &ldquo;remote&rdquo;.</p>';
        if (popular) popular.hidden = false;
        return;
      }

      var html = '<div class="prem-search__grid">';
      if (products.length) {
        html += '<div><p class="prem-search__group-title">Products</p><ul class="prem-search__products" role="list">';
        products.forEach(function (p) {
          var price = p.price ? P.formatMoney(Math.round(parseFloat(p.price) * 100)) : '';
          if (p.price_max && p.price_min && p.price_max !== p.price_min) price = 'From ' + P.formatMoney(Math.round(parseFloat(p.price_min) * 100));
          var img = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
          html += item(p.url, p.title, esc(price) + (p.available === false ? ' &middot; Sold out' : ''), img);
        });
        html += '</ul></div>';
      }
      if (collections.length || pages.length) {
        html += '<div>';
        if (collections.length) {
          html += '<p class="prem-search__group-title">Collections</p><ul class="prem-search__list" role="list">';
          collections.forEach(function (c) { html += item(c.url, c.title, '', c.featured_image && c.featured_image.url); });
          html += '</ul>';
        }
        if (pages.length) {
          html += '<p class="prem-search__group-title"' + (collections.length ? ' style="margin-top:18px"' : '') + '>Programs &amp; articles</p><ul class="prem-search__list" role="list">';
          pages.slice(0, 4).forEach(function (pg) { html += item(pg.url, pg.title, '', pg.image || (pg.featured_image && pg.featured_image.url)); });
          html += '</ul>';
        }
        html += '</div>';
      }
      html += '</div><a class="prem-link-arrow prem-search__all" href="' + esc(allUrl) + '">See all results for &ldquo;' + esc(q) +
        '&rdquo; <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 8h13M9 3l5 5-5 5"/></svg></a>';
      results.innerHTML = html;
    }
  }

  /* ==========================================================================
     Drawers (menu + cart)
     ========================================================================== */
  var currentDrawer = null, returnFocus = null;

  function openDrawer(id, opener) {
    var dr = d.getElementById(id);
    if (!dr) return false;
    if (currentDrawer && currentDrawer !== dr) closeDrawer(true);
    closeMega();
    closeSearch();
    if (currentDrawer !== dr) {
      returnFocus = opener || d.activeElement;
      lock();
    }
    currentDrawer = dr;
    dr.classList.add('is-open');
    overlay('drawer').classList.add('is-visible');
    qsa('[aria-controls="' + id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    setTimeout(function () {
      var target = qs('[data-prem-autofocus]', dr) || qs('[data-prem-drawer-close]', dr) || dr;
      target.focus({ preventScroll: true });
    }, 80);
    dispatch('prem:drawer:open', { id: id });
    return true;
  }

  function closeDrawer(silent) {
    if (!currentDrawer) return;
    var dr = currentDrawer;
    currentDrawer = null;
    dr.classList.remove('is-open');
    qsa('[aria-controls="' + dr.id + '"]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    unlock();
    if (!silent) {
      overlay('drawer').classList.remove('is-visible');
      if (returnFocus && returnFocus.focus) returnFocus.focus({ preventScroll: true });
    }
    dispatch('prem:drawer:close', { id: dr.id });
  }

  P.openDrawer = openDrawer;
  P.closeDrawer = closeDrawer;

  function initDrawers() {
    // Drawers rendered inside a sticky section inherit its stacking context and end up
    // under the page overlay, so park every drawer directly under <body>.
    qsa('[data-prem-drawer]').forEach(function (dr) {
      if (dr.parentNode === d.body) return;
      var stale = d.body.querySelector(':scope > #' + dr.id);
      if (stale && stale !== dr) stale.remove();
      d.body.appendChild(dr);
    });

    overlay('drawer').addEventListener('click', function () { closeDrawer(); });

    d.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-prem-drawer-open]');
      if (opener) { e.preventDefault(); openDrawer(opener.getAttribute('data-prem-drawer-open'), opener); return; }
      var closer = e.target.closest('[data-prem-drawer-close]');
      if (closer && currentDrawer && currentDrawer.contains(closer)) { e.preventDefault(); closeDrawer(); }
    });

    d.addEventListener('keydown', function (e) {
      if (!currentDrawer) return;
      if (e.key === 'Escape') { closeDrawer(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables(currentDrawer);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && (d.activeElement === first || d.activeElement === currentDrawer)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && d.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    var desktop = window.matchMedia('(min-width: 1025px)');
    var onChange = function () { if (desktop.matches && currentDrawer && currentDrawer.id === 'PremMenuDrawer') closeDrawer(); };
    if (desktop.addEventListener) desktop.addEventListener('change', onChange);
  }

  /* ==========================================================================
     Cart (AJAX API + section rendering)
     ========================================================================== */
  var Cart = (P.cart = {
    sections: function () {
      var ids = [];
      if (d.getElementById(CART_DRAWER_ID)) ids.push(CART_SECTION);
      qsa('[data-prem-cart-page]').forEach(function (el) { ids.push(el.getAttribute('data-section-id')); });
      return ids;
    },

    request: function (url, body) {
      var ids = this.sections();
      if (ids.length) { body.sections = ids.join(','); body.sections_url = window.location.pathname; }
      return fetch(url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok || data.status) {
            var err = new Error(data.description || data.message || 'Something went wrong. Please try again.');
            err.data = data;
            throw err;
          }
          return data;
        });
      });
    },

    add: function (items) {
      var self = this;
      return this.request(routes.cart_add, { items: items }).then(function (data) {
        self.render(data.sections, { added: (data.items || []).map(function (i) { return String(i.variant_id); }) });
        dispatch('prem:cart:added', { items: data.items });
        return data;
      });
    },

    change: function (line, quantity) {
      var self = this;
      return this.request(routes.cart_change, { line: line, quantity: quantity }).then(function (data) {
        self.render(data.sections);
        dispatch('prem:cart:changed', { cart: data });
        return data;
      });
    },

    note: function (note) {
      return fetch(routes.cart_update + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note })
      });
    },

    refresh: function () {
      var self = this, ids = this.sections();
      if (!ids.length) return Promise.resolve();
      return fetch(window.location.pathname + '?sections=' + ids.join(','))
        .then(function (r) { return r.json(); })
        .then(function (map) { self.render(map); });
    },

    render: function (map, opts) {
      if (!map) return;
      opts = opts || {};
      Object.keys(map).forEach(function (id) {
        var html = map[id];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (id === CART_SECTION) {
          var fresh = qs('[data-prem-cart-inner]', doc);
          var current = qs('#' + CART_DRAWER_ID + ' [data-prem-cart-inner]');
          if (fresh && current) current.replaceWith(fresh);
          var count = fresh ? parseInt(fresh.getAttribute('data-item-count'), 10) : NaN;
          if (!isNaN(count)) Cart.updateCount(count);
        } else {
          var page = qs('[data-prem-cart-page][data-section-id="' + id + '"]');
          var next = qs('[data-prem-cart-page]', doc);
          if (page && next) {
            if (page.getAttribute('data-state') === next.getAttribute('data-state')) {
              // Same layout: swap only the tagged regions so express checkout buttons
              // and a half-typed order note survive the update.
              qsa('[data-prem-cart-region]', next).forEach(function (region) {
                var name = region.getAttribute('data-prem-cart-region');
                var cur = qs('[data-prem-cart-region="' + name + '"]', page);
                if (cur) cur.replaceWith(region);
              });
              page.setAttribute('data-item-count', next.getAttribute('data-item-count'));
            } else {
              page.replaceWith(next);
            }
            var c2 = parseInt(next.getAttribute('data-item-count'), 10);
            if (!isNaN(c2)) Cart.updateCount(c2);
          }
        }
      });
      if (opts.added && opts.added.length) {
        var notice = qs('#' + CART_DRAWER_ID + ' [data-prem-cart-notice]');
        if (notice) notice.hidden = false;
        opts.added.forEach(function (vid) {
          qsa('[data-prem-line][data-variant-id="' + vid + '"]').forEach(function (li) { li.classList.add('is-new'); });
        });
      }
      dispatch('prem:cart:rendered');
    },

    updateCount: function (n) {
      qsa('[data-prem-cart-count]').forEach(function (el) {
        var changed = el.textContent.trim() !== String(n);
        el.textContent = n;
        el.hidden = n === 0;
        if (changed && n > 0) {
          el.classList.remove('is-bump');
          void el.offsetWidth;
          el.classList.add('is-bump');
        }
      });
      qsa('[data-prem-cart-count-label]').forEach(function (el) { el.textContent = n + (n === 1 ? ' item' : ' items'); });
    }
  });

  function setLoading(btn, state) {
    if (!btn) return;
    btn.classList.toggle('is-loading', state);
    btn.setAttribute('aria-busy', state ? 'true' : 'false');
  }

  function openCart(opener) {
    if (qs('[data-prem-cart-page]')) { window.scrollTo({ top: 0, behavior: 'smooth' }); return true; }
    return openDrawer(CART_DRAWER_ID, opener);
  }
  P.openCart = openCart;

  function initCart() {
    d.addEventListener('click', function (e) {
      // Cart icon
      var cartLink = e.target.closest('[data-prem-cart-open]');
      if (cartLink && d.getElementById(CART_DRAWER_ID) && !qs('[data-prem-cart-page]')) {
        e.preventDefault();
        openCart(cartLink);
        return;
      }

      // Quick add (cards, upsells)
      var add = e.target.closest('[data-prem-add]');
      if (add) {
        e.preventDefault();
        var qty = parseInt(add.getAttribute('data-quantity'), 10) || 1;
        setLoading(add, true);
        Cart.add([{ id: parseInt(add.getAttribute('data-prem-add'), 10), quantity: qty }])
          .then(function () {
            var card = add.closest('[data-prem-card]');
            if (card) {
              card.classList.remove('is-choosing');
              var ch = qs('[data-prem-choose]', card);
              if (ch) ch.setAttribute('aria-expanded', 'false');
            }
            openCart(add);
          })
          .catch(function (err) { P.toast(err.message); })
          .then(function () { setLoading(add, false); });
        return;
      }

      var choose = e.target.closest('[data-prem-choose]');
      if (choose) {
        var card = choose.closest('[data-prem-card]');
        card.classList.add('is-choosing');
        choose.setAttribute('aria-expanded', 'true');
        var firstOpt = qs('.prem-card__option-list button:not([disabled])', card);
        if (firstOpt) firstOpt.focus();
        return;
      }

      var cancel = e.target.closest('[data-prem-choose-cancel]');
      if (cancel) {
        var card2 = cancel.closest('[data-prem-card]');
        card2.classList.remove('is-choosing');
        var ch2 = qs('[data-prem-choose]', card2);
        if (ch2) { ch2.setAttribute('aria-expanded', 'false'); ch2.focus(); }
        return;
      }

      // Line item quantity / remove (drawer + cart page)
      var qbtn = e.target.closest('[data-prem-line-qty]');
      if (qbtn) {
        e.preventDefault();
        changeLine(qbtn, parseInt(qbtn.getAttribute('data-prem-line-qty'), 10));
        return;
      }
      var rm = e.target.closest('[data-prem-line-remove]');
      if (rm) {
        e.preventDefault();
        changeLine(rm, 0);
      }
    });

    d.addEventListener('change', function (e) {
      var input = e.target.closest('[data-prem-line-input]');
      if (input) {
        var v = Math.max(0, parseInt(input.value, 10) || 0);
        changeLine(input, v);
        return;
      }
      var note = e.target.closest('[data-prem-cart-note]');
      if (note) Cart.note(note.value);
    });

    function changeLine(el, qty) {
      var line = el.closest('[data-prem-line]');
      if (!line) return;
      line.classList.add('is-updating');
      Cart.change(parseInt(line.getAttribute('data-line'), 10), qty).catch(function (err) {
        line.classList.remove('is-updating');
        var msg = qs('[data-prem-line-error]', line);
        if (msg) { msg.textContent = err.message; msg.hidden = false; } else P.toast(err.message);
      });
    }

    // Hide the "added" notice when the drawer closes
    d.addEventListener('prem:drawer:close', function (e) {
      if (e.detail.id !== CART_DRAWER_ID) return;
      var notice = qs('#' + CART_DRAWER_ID + ' [data-prem-cart-notice]');
      if (notice) notice.hidden = true;
    });

    // Legacy theme buttons (Wokiee jQuery add-to-cart) still work: refresh + open the drawer.
    if (window.jQuery && d.getElementById(CART_DRAWER_ID)) {
      window.jQuery(d).ajaxComplete(function (event, xhr, settings) {
        if (!settings || !/\/cart\/add/.test(settings.url || '') || xhr.status >= 400) return;
        Cart.refresh().then(function () { openCart(); });
      });
    }

    // Cart restored from bfcache: re-sync counts.
    window.addEventListener('pageshow', function (e) { if (e.persisted) Cart.refresh(); });
  }

  /* ==========================================================================
     Click-to-play video (prem-video-text): nothing heavy loads until asked
     ========================================================================== */
  function initVideos() {
    d.addEventListener('click', function (e) {
      var btn = e.target.closest('.prem-video__play');
      if (!btn || btn.tagName !== 'BUTTON') return;
      var box = btn.closest('[data-prem-video]');
      if (!box || box.classList.contains('is-playing')) return;
      var label = btn.getAttribute('aria-label') || 'Video';
      var yt = box.getAttribute('data-youtube');
      var vimeo = box.getAttribute('data-vimeo');
      var el;
      if (yt || vimeo) {
        el = d.createElement('iframe');
        el.src = yt
          ? 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(yt) + '?autoplay=1&rel=0&modestbranding=1&playsinline=1'
          : 'https://player.vimeo.com/video/' + encodeURIComponent(vimeo) + '?autoplay=1';
        el.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        el.allowFullscreen = true;
        el.title = label;
      } else {
        el = d.createElement('video');
        el.src = box.getAttribute('data-src');
        el.controls = true;
        el.autoplay = true;
        el.playsInline = true;
        el.setAttribute('aria-label', label);
      }
      box.appendChild(el);
      box.classList.add('is-playing');
      if (el.play) { var pr = el.play(); if (pr && pr.catch) pr.catch(function () {}); }
      el.focus && el.focus();
    });
  }

  /* ==========================================================================
     Rotating highlight words (prem-home-hero). Pauses when off screen and
     stays on the first word for reduced-motion users.
     ========================================================================== */
  function initRotators() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    qsa('[data-prem-rotator]').forEach(function (el) {
      var words = qsa('.prem-rotator__word', el);
      if (words.length < 2) return;
      var i = 0, visible = true;
      var ms = Math.max(1500, parseInt(el.getAttribute('data-interval'), 10) || 2000);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) { visible = entries[0].isIntersecting; }).observe(el);
      }
      setInterval(function () {
        if (!visible || d.hidden) return;
        var cur = words[i];
        cur.classList.remove('is-active');
        cur.classList.add('is-leaving');
        setTimeout(function () { cur.classList.remove('is-leaving'); }, 600);
        i = (i + 1) % words.length;
        words[i].classList.add('is-active');
      }, ms);
    });
  }

  /* ==========================================================================
     Time-limited content: anything with data-prem-until="YYYY-MM-DD" disappears
     after that day (end of day, US Central). Liquid hides it server-side too;
     this covers pages served from cache.
     ========================================================================== */
  function initExpiry() {
    var now = Date.now();
    qsa('[data-prem-until]').forEach(function (el) {
      var end = Date.parse(el.getAttribute('data-prem-until') + 'T23:59:59-06:00');
      if (end && now > end) el.remove();
    });
  }

  /* ==========================================================================
     Team bios: preview + Read / Hide full bio toggle
     ========================================================================== */
  function initBios() {
    qsa('[data-prem-bio]').forEach(function (bio) {
      var intro = qs('[data-prem-bio-intro]', bio);
      var full = qs('[data-prem-bio-full]', bio);
      var btn = qs('[data-prem-bio-toggle]', bio);
      var label = qs('[data-prem-bio-label]', bio);
      if (!intro || !full || !btn) return;
      function set(open) {
        intro.hidden = open;
        full.hidden = !open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        label.textContent = open ? 'Hide full bio' : 'Read full bio';
      }
      set(false);
      btn.hidden = false;
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') !== 'true';
        set(open);
        if (!open) {
          var card = bio.closest('.prem-member') || bio;
          if (card.getBoundingClientRect().top < 0) card.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      });
    });
  }

  /* ---------- Article reading progress ---------- */
  function initProgress() {
    var bar = qs('[data-prem-progress]');
    if (!bar) return;
    var target = qs('.ra__content') || d.body;
    var raf = null;
    var tick = function () {
      raf = null;
      var r = target.getBoundingClientRect();
      var total = Math.max(1, r.height - window.innerHeight * 0.6);
      var done = Math.min(1, Math.max(0, -r.top / total));
      bar.style.width = (done * 100).toFixed(2) + '%';
    };
    window.addEventListener('scroll', function () { if (!raf) raf = requestAnimationFrame(tick); }, { passive: true });
    tick();
  }

  /* ---------- Boot ---------- */
  /* ---------- Legacy autocomplete off for Premier search forms ----------
     theme.min.js binds a jQuery keyup autocomplete to every form[action*="/search"]
     and appends a .search-results list inside it, which breaks these layouts.
     It binds on jQuery ready, so unbind once the page has loaded. */
  function detachLegacySearch() {
    var $ = window.jQuery;
    if (!$) return;
    qsa('.prem-search form, .prem-scope form[action*="/search"]').forEach(function (form) {
      $(form).find('input[name="q"]').off('keyup change');
      $(form).find('.search-results').remove();
    });
  }

  function boot() {
    if (d.readyState === 'complete') detachLegacySearch();
    else window.addEventListener('load', detachLegacySearch);
    initExpiry();
    initProgress();
    initBios();
    initVideos();
    initRotators();
    initAnnouncement();
    initHeader();
    initSearch();
    initDrawers();
    initCart();
    dispatch('prem:ready');
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
